import { useState, useCallback, useEffect, useMemo } from "react";

import { Box, Flex, Group, Radio, SimpleGrid } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";
import { useTranslation } from "react-i18next";

import {
  DECIMAL_PROPS,
  NumberInput,
  Select,
  YesNoRadioButton,
  InputLabel,
  TextInput,
  DatePicker,
  RadioGroup,
} from "components";
import SectionTitle from "pages/clients/view/components/section-title";
import CreditRequestExpenses from "pages/clients/view/credit/request/outcomes/expenses";
import { formClasses } from "styles";
import {
  BankCreditOptionsResponse,
  BankCreditRequestForm,
  BankCreditResponse,
  HousingExpense,
} from "types";

import { AddressSection } from "../personal/address-section";

import { BankCreditRequestMortgage } from "./bank-credit-request-mortgage";
import { HousingCostSharingIndividual } from "./housing-cost-sharing-individual";

const housingCostSplitOptions = [2, 3, 4, 5];

interface BankCreditRequestOutcomesProps {
  credit: BankCreditResponse;
  form: UseFormReturnType<BankCreditRequestForm>;
  visibleFields: string[];
  options: BankCreditOptionsResponse;
  readOnly?: boolean;
}

export const BankCreditRequestOutcomes = ({
  credit,
  form,
  visibleFields,
  options,
  readOnly = false,
}: BankCreditRequestOutcomesProps) => {
  const { t } = useTranslation();

  const getInitialHousingExpense = (): HousingExpense | undefined => {
    if (credit.mortgage) return "MORTGAGE";
    if (credit.rent) return "RENT";
    return undefined;
  };

  const [housingExpense, setHousingExpense] = useState<HousingExpense | undefined>(
    getInitialHousingExpense,
  );

  const changeHousingExpense = useCallback(
    (val: HousingExpense) => {
      setHousingExpense(val);
      if (val === "MORTGAGE") {
        form.setFieldValue("mortgage", true);
        form.setFieldValue("rent", false);
      } else if (val === "RENT") {
        form.setFieldValue("rent", true);
        form.setFieldValue("mortgage", false);
      }
    },
    [form],
  );

  const mortgagePaidOff = form.getValues().mortgagePaidOff;
  const finalTaxCalculation = form.getValues().finalTaxCalculation;

  useEffect(() => {
    if (mortgagePaidOff === true) {
      form.setFieldValue("mortgageExpenses", []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mortgagePaidOff]);

  useEffect(() => {
    // Note: When mortgagePaidOff === false, mortgageAmount is set by BankCreditRequestMortgage component
    if (mortgagePaidOff === true) {
      if (finalTaxCalculation) {
        const onePercentMortgage = finalTaxCalculation * 0.01;
        form.setFieldValue("mortgageAmount", onePercentMortgage);
      } else {
        form.setFieldValue("mortgageAmount", undefined);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mortgagePaidOff, finalTaxCalculation]);

  useEffect(() => {
    const formValues = form.getValues();
    if (Object.keys(formValues).length === 0) {
      return;
    }
    if (formValues.additionalExpenses === false) {
      //not using visibleFields here because expenses is an array
      form.setFieldValue("expenses", []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.getValues().additionalExpenses]);

  const housingCostOptions = useMemo(
    () => [
      {
        value: "1",
        label: t("bankCredit.housingCostSplitCount.options.NOT_SHARED"),
      },
      ...housingCostSplitOptions.map((n) => ({
        value: String(n),
        label: String(n),
      })),
    ],
    [t],
  );

  return (
    <Flex p={"md"} direction={"column"} gap={"sm"}>
      <SectionTitle label={t("credit.outcomes.sectionTitle")} isBankCredit />
      <SimpleGrid cols={4}>
        <Box>
          <InputLabel label={t("credit.mortgageOrRent.label")} />
          <Group>
            <Radio
              checked={housingExpense === "RENT"}
              onChange={() => changeHousingExpense("RENT")}
              label={t("credit.mortgageOrRent.options.RENT")}
              disabled={readOnly}
            />
            <Radio
              checked={housingExpense === "MORTGAGE"}
              onChange={() => changeHousingExpense("MORTGAGE")}
              label={t("credit.mortgageOrRent.options.MORTGAGE")}
              disabled={readOnly}
            />
          </Group>
        </Box>
        <Select
          label={t("credit.houseSituation.label")}
          data={options["houseSituation"].map((el) => ({
            value: el,
            label: t(`credit.houseSituation.options.${el}`),
          }))}
          key={form.key("houseSituation")}
          {...form.getInputProps("houseSituation")}
          clearable={true}
          disabled={readOnly}
        />
        {visibleFields.includes("housingCostSplitCount") && (
          <Select
            label={t("bankCredit.housingCostSplitCount.label")}
            data={housingCostOptions}
            key={form.key("housingCostSplitCount")}
            value={
              form.getValues().housingCostSplitCount
                ? String(form.getValues().housingCostSplitCount)
                : null
            }
            onChange={(value) =>
              form.setFieldValue("housingCostSplitCount", value ? Number(value) : undefined)
            }
            error={form.errors.housingCostSplitCount}
            clearable={true}
            disabled={readOnly}
          />
        )}
      </SimpleGrid>

      <HousingCostSharingIndividual
        credit={credit}
        form={form}
        readOnly={readOnly}
        housingExpense={housingExpense}
      />

      <SimpleGrid cols={4}>
        {visibleFields.includes("rentAmountBn") && (
          <NumberInput
            label={t("bankCredit.rentAmountBn.label")}
            key={form.key("rentAmountBn")}
            {...form.getInputProps("rentAmountBn")}
            min={0}
            {...DECIMAL_PROPS}
            disabled={readOnly}
          />
        )}
        {visibleFields.includes("additionalHousingExpensesIncluded") && (
          <YesNoRadioButton
            label={t("bankCredit.additionalHousingExpensesIncluded.label")}
            name={"additionalHousingExpensesIncluded"}
            form={form}
            disabled={readOnly}
          />
        )}
        {visibleFields.includes("rentAmountCb") && (
          <NumberInput
            label={t("bankCredit.rentAmountCb.label")}
            key={form.key("rentAmountCb")}
            {...form.getInputProps("rentAmountCb")}
            min={0}
            {...DECIMAL_PROPS}
            disabled={readOnly}
          />
        )}
        {visibleFields.includes("rentAmountCc") && (
          <NumberInput
            label={t("bankCredit.rentAmountCc.label")}
            key={form.key("rentAmountCc")}
            {...form.getInputProps("rentAmountCc")}
            min={0}
            {...DECIMAL_PROPS}
            disabled={readOnly}
          />
        )}

        {visibleFields.includes("mortgagePaidOff") && (
          <YesNoRadioButton
            label={t("bankCredit.mortgagePaidOff.label")}
            name={"mortgagePaidOff"}
            onChange={() => form.setFieldValue("mortgageAmount", undefined)}
            form={form}
            disabled={readOnly}
          />
        )}
        {visibleFields.includes("finalTaxCalculation") && (
          <NumberInput
            label={t("bankCredit.finalTaxCalculation.label")}
            key={form.key("finalTaxCalculation")}
            {...form.getInputProps("finalTaxCalculation")}
            min={0}
            disabled={readOnly}
          />
        )}
        {visibleFields.includes("mortgageAmount") && (
          <NumberInput
            label={t("credit.mortgageAmount.label")}
            key={form.key("mortgageAmount")}
            {...form.getInputProps("mortgageAmount")}
            min={0}
            {...DECIMAL_PROPS}
            disabled={true}
          />
        )}
        {visibleFields.includes("includeAmortization") && (
          <YesNoRadioButton
            label={t("bankCredit.includeAmortization.label")}
            name={"includeAmortization"}
            form={form}
            disabled={readOnly}
          />
        )}
        {visibleFields.includes("amortizationAmount") && (
          <NumberInput
            label={t("bankCredit.amortizationAmount.label")}
            key={form.key("amortizationAmount")}
            {...form.getInputProps("amortizationAmount")}
            min={0}
            disabled={readOnly}
          />
        )}
        {visibleFields.includes("amortizationType") && (
          <RadioGroup
            fieldName="amortizationType"
            label={t("bankCredit.amortizationType.label")}
            options={options["amortizationType"].map((type) => ({
              value: type,
              label: t(`bankCredit.amortizationType.options.${type}`),
            }))}
            form={form}
            disabled={readOnly}
            required={false}
          />
        )}
      </SimpleGrid>

      {mortgagePaidOff === false && (
        <BankCreditRequestMortgage credit={credit} form={form} readOnly={readOnly} />
      )}

      {visibleFields.includes("mortgageFrom") &&
        visibleFields.includes("residenceSameAsMortgageAddress") && (
          <SimpleGrid cols={4} mt={"xl"}>
            <DatePicker
              label={t(`bankCredit.mortgageFrom.label`)}
              key={form.key("mortgageFrom")}
              {...form.getInputProps("mortgageFrom")}
              maxDate={new Date()}
              disabled={readOnly}
            />

            <YesNoRadioButton
              label={t("bankCredit.residenceSameAsMortgageAddress.label")}
              name={"residenceSameAsMortgageAddress"}
              form={form}
              disabled={readOnly}
            />
          </SimpleGrid>
        )}

      {visibleFields.includes("mortgagePostal") &&
        visibleFields.includes("mortgageCity") &&
        visibleFields.includes("mortgageStreet") &&
        visibleFields.includes("mortgageHouseNumber") &&
        visibleFields.includes("mortgageCanton") && (
          <AddressSection
            form={form}
            sectionTitle={t("credit.mortgageAddress.sectionTitle")}
            fieldNames={{
              postal: "mortgagePostal",
              city: "mortgageCity",
              street: "mortgageStreet",
              houseNumber: "mortgageHouseNumber",
              canton: "mortgageCanton",
            }}
            cantonList={options["SwissCanton"]}
            readOnly={readOnly}
          />
        )}

      <Box mt={"sm"} className={formClasses.sectionBorder}>
        <SectionTitle label={t("credit.healthInsurance.sectionTitle")} isBankCredit />
        <SimpleGrid cols={4}>
          <NumberInput
            label={t("credit.healthInsuranceAmount.label")}
            key={form.key("healthInsuranceAmount")}
            {...form.getInputProps("healthInsuranceAmount")}
            min={0}
            {...DECIMAL_PROPS}
            disabled={readOnly}
          />
          <YesNoRadioButton
            label={t("credit.healthInsuranceDiscount.label")}
            name={"healthInsuranceDiscount"}
            form={form}
            disabled={readOnly}
          />
        </SimpleGrid>
      </Box>

      <Box mt={"sm"} pt={"sm"} className={formClasses.sectionBorder}>
        <SimpleGrid cols={4}>
          <YesNoRadioButton
            label={t(`credit.expenseType.anotherExpense.label`)}
            name={"additionalExpenses"}
            form={form}
            disabled={readOnly}
          />
        </SimpleGrid>
      </Box>

      {form.getValues().additionalExpenses && (
        <CreditRequestExpenses
          credit={credit}
          form={form}
          options={options}
          readOnly={readOnly}
          isBankCredit
        />
      )}

      <Box mt={"sm"} pt="sm" className={formClasses.sectionBorder}>
        <SimpleGrid cols={4}>
          <YesNoRadioButton
            label={t("credit.enforcedCollection.label")}
            name={"enforcedCollection"}
            form={form}
            disabled={readOnly}
          />
        </SimpleGrid>
      </Box>

      <SimpleGrid cols={4} mt={"lg"}>
        <TextInput
          label={t("credit.comment.singular")}
          key={form.key("comment4")}
          {...form.getInputProps("comment4")}
          disabled={readOnly}
        />
      </SimpleGrid>
    </Flex>
  );
};
